import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalysisV2Modal } from '@/components/ui-v2/analysis-v2-modal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  type WatchDealSelectionExportsSubscription,
  useExportDealForAnalyseMutation,
  useWatchDealSelectionExportsSubscription,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { mapI18nLanguageToLanguageIso6391 } from '@/lib/utils';
import { ExportStatus } from '@/types/deal-selection-export';
import Button from '@appchoose/button';
import { FileSpreadsheet, Loader2, Sparkles } from 'lucide-react';

type DealSelectionExportFromSubscription =
  WatchDealSelectionExportsSubscription['dealSelectionExportsUpdated'][number];

type RefetchDealResult = {
  data?: {
    deal?: {
      googleAnalyzeFileUrl?: string | null;
    } | null;
  };
};

const getAnalyzeFileUrlFromRefetch = (result: unknown): string | undefined => {
  if (!result || typeof result !== 'object' || !('data' in result)) {
    return undefined;
  }

  return (result as RefetchDealResult).data?.deal?.googleAnalyzeFileUrl?.trim();
};

const openAnalyzeFile = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const getPendingExportStorageKey = (dealId: string) =>
  `analysis-v2-export:${dealId}`;

function AnalysisV2Button() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { deal, refetchDeal } = useDealContext();
  const [exportDealForAnalyse, { loading }] = useExportDealForAnalyseMutation();
  const pendingExportStorageKey = getPendingExportStorageKey(deal.id);
  const [pendingExportId, setPendingExportId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const analyzeFileUrl = deal.googleAnalyzeFileUrl?.trim();
  const hasAnalyzeFileUrl = Boolean(analyzeFileUrl);
  const isExportInProgress = !hasAnalyzeFileUrl && Boolean(pendingExportId);
  const label = hasAnalyzeFileUrl
    ? t('general.open')
    : t('components.offer_actions_header.analysis_v2_button');

  const updatePendingExportId = useCallback(
    (exportId: string | null) => {
      setPendingExportId(exportId);

      if (typeof window === 'undefined') return;

      if (exportId) {
        window.localStorage.setItem(pendingExportStorageKey, exportId);
      } else {
        window.localStorage.removeItem(pendingExportStorageKey);
      }
    },
    [pendingExportStorageKey]
  );

  const handleTrackedExportStatus = useCallback(
    (exportItem: DealSelectionExportFromSubscription) => {
      if (exportItem.id !== pendingExportId) return;
      if (exportItem.status === ExportStatus.Pending) return;

      updatePendingExportId(null);

      if (exportItem.status === ExportStatus.Completed) {
        void refetchDeal();
      }
    },
    [pendingExportId, refetchDeal, updatePendingExportId]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPendingExportId(window.localStorage.getItem(pendingExportStorageKey));
  }, [pendingExportStorageKey]);

  useEffect(() => {
    if (!hasAnalyzeFileUrl || !pendingExportId) return;
    updatePendingExportId(null);
  }, [hasAnalyzeFileUrl, pendingExportId, updatePendingExportId]);

  const trackedSelectionExport = useMemo(() => {
    if (!pendingExportId) return undefined;
    return deal.selectionExports.find(({ id }) => id === pendingExportId);
  }, [deal.selectionExports, pendingExportId]);

  useEffect(() => {
    if (!trackedSelectionExport) return;
    if (trackedSelectionExport.status === ExportStatus.Pending) return;

    updatePendingExportId(null);

    if (trackedSelectionExport.status === ExportStatus.Completed) {
      void refetchDeal();
    }
  }, [refetchDeal, trackedSelectionExport, updatePendingExportId]);

  useWatchDealSelectionExportsSubscription({
    variables: { dealId: deal.id },
    skip: !pendingExportId,
    onSubscriptionData({ subscriptionData }) {
      const updated = subscriptionData.data?.dealSelectionExportsUpdated;
      const exportItem = updated?.find(({ id }) => id === pendingExportId);
      if (!exportItem) return;

      handleTrackedExportStatus(exportItem);
    },
  });

  const handleClick = useCallback(() => {
    if (analyzeFileUrl) {
      openAnalyzeFile(analyzeFileUrl);
      return;
    }

    trackEvent('ANALYSIS_V2_BUTTON_CLICKED', {
      dealId: deal.id,
    });

    setIsModalOpen(true);
  }, [analyzeFileUrl, deal.id]);

  const handleLaunch = useCallback(
    async (prompt?: string) => {
      trackEvent('ANALYSIS_V2_VALIDATED', {
        dealId: deal.id,
        hasPrompt: Boolean(prompt),
      });

      try {
        const { data } = await exportDealForAnalyse({
          variables: {
            dealId: deal.id,
            language: mapI18nLanguageToLanguageIso6391(i18n.language),
            prompt: prompt
              ? `Utilise les catégories suivantes fournies par l'utilisateur pour recatégoriser les produits : \n\n${prompt}`
              : 'Here is the gsheet :\n',
          },
        });

        const result = data?.exportDealForAnalyse;
        if (result?.__typename === 'DomainException') {
          updatePendingExportId(null);
          toast({
            title: t('general.error'),
            description: result.code,
            variant: 'destructive',
          });
          return;
        }

        if (result?.__typename !== 'MutationSuccess') {
          updatePendingExportId(null);
          toast({
            title: t('general.error'),
            description: t('components.other.analysis_export_error'),
            variant: 'destructive',
          });
          return;
        }

        updatePendingExportId(result.id);
        setIsModalOpen(false);

        const refetchResult = await refetchDeal();
        const nextAnalyzeFileUrl = getAnalyzeFileUrlFromRefetch(refetchResult);

        if (nextAnalyzeFileUrl) {
          updatePendingExportId(null);
          openAnalyzeFile(nextAnalyzeFileUrl);
          return;
        }

        toast({
          title: t('general.success'),
          description: t('components.other.analysis_export_started'),
        });
      } catch (error) {
        toast({
          title: t('general.error'),
          description:
            error instanceof Error
              ? error.message
              : t('components.other.analysis_export_error'),
          variant: 'destructive',
        });
        updatePendingExportId(null);
      }
    },
    [
      deal.id,
      exportDealForAnalyse,
      i18n.language,
      refetchDeal,
      t,
      toast,
      updatePendingExportId,
    ]
  );

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              onClick={handleClick}
              disabled={loading || isExportInProgress}
              appearance="outline"
              className="flex items-center gap-2 border border-gray-300 font-medium"
            >
              {loading || isExportInProgress ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : hasAnalyzeFileUrl ? (
                <FileSpreadsheet className="h-4 w-4" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {label}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t('components.offer_actions_header.analysis_v2_button')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AnalysisV2Modal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onLaunch={handleLaunch}
        loading={loading || isExportInProgress}
      />
    </>
  );
}

export default AnalysisV2Button;
